// Trailer thumbnail component — sprite-backed scrub on hover when the
// demo has a generated thumbnail sprite, falls back to a lazy
// <video preload="metadata"> when it doesn't.
//
// Forked from App.jsx so non-App contexts (Decision Rooms detail view,
// future home dashboard cards, etc.) can use the same visual without
// pulling all of App.jsx's other concerns. The logic mirrors the
// original — kept as one file so future tweaks land in both places
// or get consolidated when we feel like deduping.

import React, { useEffect, useRef, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

// Must match backend/utils/generateThumbnailSprite.js — backend
// renders a 10-frame strip; the CSS background-position math here
// assumes the same count.
const REEL_SPRITE_FRAMES = 10;

function posterFrameIndex(demoId) {
  if (!demoId || typeof demoId !== 'string') return Math.floor(REEL_SPRITE_FRAMES / 2);
  let h = 5381;
  for (let i = 0; i < demoId.length; i++) {
    h = ((h << 5) + h + demoId.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % REEL_SPRITE_FRAMES;
}

function posterSeekFraction(demoId) {
  if (!demoId || typeof demoId !== 'string') return 0.5;
  let h = 5381;
  for (let i = 0; i < demoId.length; i++) {
    h = ((h << 5) + h + demoId.charCodeAt(i)) | 0;
  }
  return 0.1 + (Math.abs(h) % 81) / 100;
}

// The backend now picks a poster frame from the trailer's own content
// and ships it as `posterFrameIndex`. That beats a hash of the UUID,
// which was only ever "stable and varied", never "a good frame". The
// hash stays as the fallback for rows the backend hasn't scored --
// legacy trailers, and any deploy where this frontend is ahead of the
// API (the field is simply absent then, which reads as undefined).
function resolvePosterFrame(explicit, demoId) {
  if (Number.isInteger(explicit) && explicit >= 0 && explicit < REEL_SPRITE_FRAMES) {
    return explicit;
  }
  return posterFrameIndex(demoId);
}

// Video-fallback equivalent: aim at the middle of the chosen frame's
// bucket, since frame N covers [N/10, (N+1)/10] of the runtime.
function resolveSeekFraction(explicit, demoId) {
  if (Number.isInteger(explicit) && explicit >= 0 && explicit < REEL_SPRITE_FRAMES) {
    return (explicit + 0.5) / REEL_SPRITE_FRAMES;
  }
  return posterSeekFraction(demoId);
}

const ReelThumb = React.memo(function ReelThumb({ demoId, hasSprite, className, posterFrame }) {
  if (hasSprite) return <ReelThumbSprite demoId={demoId} className={className} posterFrame={posterFrame} />;
  return <ReelThumbVideo demoId={demoId} className={className} posterFrame={posterFrame} />;
});

const ReelThumbSprite = React.memo(function ReelThumbSprite({ demoId, className, posterFrame }) {
  const ref = useRef(null);
  const rectRef = useRef(null);
  const rafRef = useRef(0);
  const pendingXRef = useRef(0);

  const midIndex = resolvePosterFrame(posterFrame, demoId);
  const [frame, setFrame] = useState(midIndex);
  // The resting frame can arrive after first paint (the list renders,
  // then a refetch fills posterFrameIndex in). Re-seat it when it
  // changes -- but never mid-scrub, which is exactly when rectRef is
  // non-null.
  useEffect(() => {
    if (rectRef.current) return;
    setFrame(midIndex);
  }, [midIndex]);

  const applyPendingFrame = () => {
    rafRef.current = 0;
    const rect = rectRef.current;
    if (!rect || rect.width <= 0) return;
    const ratio = Math.max(0, Math.min(1, pendingXRef.current / rect.width));
    const next = Math.min(
      REEL_SPRITE_FRAMES - 1,
      Math.max(0, Math.floor(ratio * REEL_SPRITE_FRAMES))
    );
    setFrame((prev) => (prev === next ? prev : next));
  };

  const handleEnter = (e) => { rectRef.current = e.currentTarget.getBoundingClientRect(); };
  const handleMove = (e) => {
    if (!rectRef.current) rectRef.current = e.currentTarget.getBoundingClientRect();
    pendingXRef.current = e.clientX - rectRef.current.left;
    if (!rafRef.current) rafRef.current = requestAnimationFrame(applyPendingFrame);
  };
  const handleLeave = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    rectRef.current = null;
    setFrame(midIndex);
  };

  const bgStyle = {
    backgroundImage: `url(${API_BASE}/api/thumbnails/${demoId})`,
    backgroundSize: `${REEL_SPRITE_FRAMES * 100}% 100%`,
    backgroundPosition: `${(frame / (REEL_SPRITE_FRAMES - 1)) * 100}% 0`,
    backgroundRepeat: 'no-repeat',
  };

  return (
    <div
      ref={ref}
      className={`${className || ''} reel-thumb-sprite`}
      style={bgStyle}
      onMouseEnter={handleEnter}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      aria-hidden="true"
    />
  );
});

const ReelThumbVideo = React.memo(function ReelThumbVideo({ demoId, className, posterFrame }) {
  const ref = useRef(null);
  const seekToMid = () => {
    const v = ref.current;
    if (!v) return;
    const dur = v.duration;
    if (!isFinite(dur) || dur <= 0) return;
    try { v.currentTime = dur * resolveSeekFraction(posterFrame, demoId); } catch { /* noop */ }
  };
  const handleEnter = () => {
    const v = ref.current;
    if (!v) return;
    v.play().catch(() => {});
  };
  const handleLeave = () => {
    const v = ref.current;
    if (!v) return;
    v.pause();
    seekToMid();
  };
  return (
    <video
      ref={ref}
      src={`${API_BASE}/api/stream/${demoId}`}
      preload="metadata"
      muted
      playsInline
      className={className}
      onLoadedMetadata={seekToMid}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    />
  );
});

export default ReelThumb;
